import { computed, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { AuthAccess } from '../services/auth-access';
import { AuthRoles } from '../models/auth-roles';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';

interface AuthState {
  isAuthenticated: boolean;
  isDoneLoading: boolean;
  identityClaims: Record<string, unknown>;
  accessToken: string;
  idToken: string;
}

const initalState: AuthState = {
  isAuthenticated: false,
  isDoneLoading: false,
  identityClaims: {},
  accessToken: '',
  idToken: '',
};

export const AuthStore = signalStore(
  { providedIn: 'root' },
  withState(initalState),
  withProps(() => ({
    _authAccess: inject(AuthAccess),
  })),
  withComputed(({ identityClaims }) => ({
    roles: computed(() => (identityClaims()['roles'] as string[]) ?? []),
    isAsaAdmin: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.ASA_Admin);
    }),
    isAstraAdmin: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.ASTRA_Admin);
    }),
    isSTVAAdmin: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.STVA_Admin);
    }),
    isVisionAdmin: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.Vision_Admin);
    }),
    isVisionUser: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.Vision_User);
    }),
    isNhkAdmin: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.NHK_Admin);
    }),
    isNhkUser: computed(() => {
      const roles = (identityClaims()['roles'] as string[]) ?? [];
      return !!roles?.includes(AuthRoles.NHK_User);
    }),
  })),
  withMethods(store => ({
    login(targetUrl?: string) {
      store._authAccess.login(targetUrl);
    },
    logout() {
      store._authAccess.logout();
    },
    _isDoneLoading: rxMethod<boolean>(
      pipe(
        tap(isDoneLoading => {
          patchState(store, { isDoneLoading: isDoneLoading });
        })
      )
    ),
    _loadData: rxMethod<boolean>(
      pipe(
        tap(isAuthenticated => {
          if (isAuthenticated) {
            patchState(store, {
              isAuthenticated: true,
              identityClaims: store._authAccess.identityClaims,
              accessToken: store._authAccess.accessToken,
              idToken: store._authAccess.idToken,
            });
            return;
          }

          patchState(store, {
            isAuthenticated: false,
            identityClaims: {},
            accessToken: '',
            idToken: '',
          });
        })
      )
    ),
  })),
  withHooks(store => ({
    onInit: () => {
      store._isDoneLoading(store._authAccess.isDoneLoading$);
      store._loadData(store._authAccess.isAuthenticated$);
    },
  }))
);
